import { useFeaturedSessionsMutations } from "~/pages/manifold-nextgen/components/projects/useFeaturedSessionsMutations";
import { FeaturedSession } from "~/services/projects";

export interface UseFeaturedToggle {
  isFeatured: boolean;
  isMutating: boolean;
  // `shareToken` is only meaningful when `next` is true — featuring requires
  // a share link, so the caller passes the token from generating one.
  handleToggle: (next: boolean, shareToken?: string) => Promise<void>;
}

// Featuring is coupled to sharing: a session can only be pinned to the rail
// via its share token, so this derives "is this conversation featured" by
// matching its current share token against the project's rail rather than
// tracking its own state — there's no flag on the conversation anymore.
export const useFeaturedToggle = (
  projectId: string,
  featuredSessions: FeaturedSession[] | undefined,
  shareToken: string | undefined,
  displayName: string,
  agentId: string | null | undefined
): UseFeaturedToggle => {
  const { isMutating, feature, unfeature } = useFeaturedSessionsMutations(projectId);
  const entry = shareToken
    ? featuredSessions?.find((f) => f.share_token === shareToken)
    : undefined;

  const handleToggle = async (next: boolean, tokenOverride?: string) => {
    if (next) {
      const token = tokenOverride ?? shareToken;
      if (!token) {
        return;
      }
      await feature({
        share_token: token,
        display_name: displayName,
        agent_id: agentId ?? undefined,
      });
    } else if (entry) {
      await unfeature(entry);
    }
  };

  return { isFeatured: Boolean(entry), isMutating, handleToggle };
};
